import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import usePresenceHeartbeat from "@/hooks/usePresenceHeartbeat";
import useViewport from "@/lib/useViewport";
import { Toaster } from "sonner";
import Layout from "@/components/Layout";
import AdminLayout from "@/components/AdminLayout";
import MobileAdminSwitch from "@/components/MobileAdminSwitch";
import LandingPage from "@/pages/LandingPage";
import FreeAliasPage from "@/pages/FreeAliasPage";
import ClaimPage from "@/pages/ClaimPage";
// LoginPage import removed — /login now redirects to /?focus=signin (Landing SIGN IN is canonical)
import RegisterPage from "@/pages/RegisterPage";
import AuthCallback from "@/pages/AuthCallback";
import InboxPage from "@/pages/InboxPage";
import AliasPage from "@/pages/AliasPage";
import CategoriesPage from "@/pages/CategoriesPage";
import LabelsPage from "@/pages/LabelsPage";
import SettingsPage from "@/pages/SettingsPage";
import AdminOverview from "@/pages/AdminOverview";
import AdminUsersPage from "@/pages/AdminUsersPage";
import AdminActivityPage from "@/pages/AdminActivityPage";
import AdminPage from "@/pages/AdminPage";
import Admin2FASetup from "@/pages/Admin2FASetup";
import AdminLoginPage from "@/pages/AdminLoginPage";
import PricingPage from "@/pages/PricingPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import SharePage from "@/pages/SharePage";
import ContactsPage from "@/pages/ContactsPage";
import TeamPage from "@/pages/TeamPage";
import InviteAcceptPage from "@/pages/InviteAcceptPage";
import ProLoginPage from "@/pages/ProLoginPage";
import ProRecoverPage from "@/pages/ProRecoverPage";
import ProDashboardPage from "@/pages/ProDashboardPage";
import ProPaymentSuccessPage from "@/pages/ProPaymentSuccessPage";
import SetupPasswordPage from "@/pages/SetupPasswordPage";
import ProReadyScreen from "@/pages/ProReadyScreen";
import ProInboxPage from "@/pages/ProInboxPage";
import RecoveryCodesPage from "@/pages/RecoveryCodesPage";
import { initPostHog } from "@/lib/analytics";
import AdminGrowthPage from "@/pages/AdminGrowthPage";
import AdminSecurityPage from "@/pages/AdminSecurityPage";
import ImpersonatePage from "@/pages/ImpersonatePage";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import ReferralCapturePage from "@/pages/ReferralCapturePage";
import AdminReferralsPage from "@/pages/AdminReferralsPage";
import "@/App.css";

// Initialize PostHog (silent no-op if key not set)
initPostHog();

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-white" data-testid="loading-screen">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-zinc-500">Loading...</p>
        </div>
      </div>
    );
  }
  // PROMPT 026 — unauthenticated users are routed to /pricing so they
  // see plans first; the pricing page exposes a Sign-in path for users
  // who already have an account.
  if (!user) return <Navigate to="/pricing" replace />;
  // Block access if recovery codes not confirmed (skip for admin/superadmin and legacy users without the flag)
  if (
    user.recovery_codes_generated === true &&
    user.recovery_codes_confirmed === false &&
    location.pathname !== "/recovery-codes"
  ) {
    return <Navigate to="/recovery-codes" state={{ email: user.email }} replace />;
  }
  return children;
}

function AdminGuard({ children }) {
  const { user, loading, logout } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-white" data-testid="admin-loading">
        <div className="w-8 h-8 border-2 border-zinc-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  // Unauthenticated → dedicated admin login (never the shared /login).
  if (!user) {
    const next = encodeURIComponent(location.pathname + (location.search || ""));
    return <Navigate to={`/housekeeping/login?next=${next}`} replace />;
  }
  // Non-admin → DO NOT bleed into /inbox. Clear stale session and bounce
  // back to the housekeeping login. A regular-user session must never
  // satisfy an admin route guard, even silently.
  if (user.role !== "admin" && user.role !== "superadmin") {
    // Fire-and-forget clear; the navigate below renders before this resolves.
    logout?.().catch(() => {});
    return <Navigate to="/housekeeping/login" replace />;
  }
  return children;
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AppRouter() {
  const location = useLocation();

  // CORE_001 — mount the viewport hook once, near the top of the tree.
  // Its only purpose at this level is to keep the global CSS variables
  // (--vvh-px, --kb-inset) in sync so plain CSS / Tailwind utilities
  // can react without each component subscribing.
  useViewport();

  // CRITICAL: Detect session_id synchronously during render (before ProtectedRoute runs)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/free" element={<FreeAliasPage />} />
      <Route path="/free/:claimCode" element={<FreeAliasPage />} />
      <Route path="/claim" element={<ClaimPage />} />
      <Route path="/s/:token" element={<SharePage />} />
      <Route path="/r/:code" element={<ReferralCapturePage />} />
      {/* Legacy /login — canonical sign-in now lives on Landing (/?focus=signin) */}
      <Route path="/login" element={<Navigate to="/?focus=signin" replace />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/recovery-codes" element={<RecoveryCodesPage />} />
      <Route path="/pro/login" element={<ProLoginPage />} />
      <Route path="/pro/recover" element={<ProRecoverPage />} />
      <Route path="/pro/dashboard" element={<ProDashboardPage />} />
      <Route path="/pro/payment-success" element={<ProPaymentSuccessPage />} />
      <Route path="/setup-password/:token" element={<SetupPasswordPage />} />
      <Route path="/pro/ready" element={<ProReadyScreen />} />
      <Route path="/pro/inbox" element={<ProInboxPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/impersonate" element={<ImpersonatePage />} />
      <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>} />
      <Route path="/payment/cancel" element={<PricingPage />} />

      {/* Authenticated routes */}
      <Route path="/inbox" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<InboxPage />} />
      </Route>
      <Route path="/aliases" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<AliasPage />} />
      </Route>
      <Route path="/categories" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<CategoriesPage />} />
      </Route>
      <Route path="/labels" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<LabelsPage />} />
      </Route>
      <Route path="/contacts" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<ContactsPage />} />
      </Route>
      <Route path="/team" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<TeamPage />} />
      </Route>
      <Route path="/invite/:token" element={<InviteAcceptPage />} />
      <Route path="/settings" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<SettingsPage />} />
      </Route>
      {/* Admin routes — separate layout, admin role required */}
      <Route path="/admin/setup-2fa" element={<Admin2FASetup />} />
      <Route path="/housekeeping/login" element={<AdminLoginPage />} />
      <Route path="/admin" element={<AdminGuard><AdminLayout /></AdminGuard>}>
        <Route index element={<AdminOverview />} />
        <Route path="users" element={<AdminUsersPage />} />
        <Route path="growth" element={<AdminGrowthPage />} />
        <Route path="activity" element={<AdminActivityPage />} />
        <Route path="security" element={<AdminSecurityPage />} />
        <Route path="pricing" element={<AdminPage tab="pricing" />} />
        <Route path="experiments" element={<AdminPage tab="experiments" />} />
        <Route path="funnel" element={<AdminPage tab="funnel" />} />
        <Route path="referrals" element={<AdminReferralsPage />} />
      </Route>
      {/* /housekeeping — production admin entry point (same as /admin) */}
      <Route path="/housekeeping" element={<AdminGuard><AdminLayout /></AdminGuard>}>
        <Route index element={<AdminOverview />} />
        <Route path="users" element={<AdminUsersPage />} />
        <Route path="growth" element={<AdminGrowthPage />} />
        <Route path="activity" element={<AdminActivityPage />} />
        <Route path="security" element={<AdminSecurityPage />} />
        <Route path="pricing" element={<AdminPage tab="pricing" />} />
        <Route path="experiments" element={<AdminPage tab="experiments" />} />
        <Route path="funnel" element={<AdminPage tab="funnel" />} />
        <Route path="referrals" element={<AdminReferralsPage />} />
      </Route>
    </Routes>
  );
}

function ImpersonationWrapper() {
  const { user } = useAuth();
  const isImpersonating = typeof window !== "undefined" && sessionStorage.getItem("impersonate_active") === "true";
  if (!isImpersonating || !user) return null;
  return <ImpersonationBanner adminEmail={user._impersonated_by} />;
}

function PresenceHeartbeat() {
  const { user } = useAuth();
  usePresenceHeartbeat(user?._id || user?.id);
  return null;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster
          position="top-right"
          richColors
          closeButton
          expand
          gap={10}
          offset={16}
        />
        <ImpersonationWrapper />
        <PresenceHeartbeat />
        <AppRouter />
        {/* Global mobile bottom switch for admins. Renders ONCE at app root
            so its position is identical across all views. Self-guards on
            role, viewport, and path (see component). */}
        <MobileAdminSwitch />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
